import { useState, useEffect, Fragment } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import Loader from '../helpers/Loader'
import get from 'lodash/get'
import API from 'api'
import Modal from 'react-modal'

function CourseDescriptionPopup({ id, isOpen, slug, onClose }) {
  const [isLoading, setIsLoading] = useState(true)
  const [course, setCourse] = useState({
    activity: {},
  })
  const [expandedSections, setExpanded] = useState({
    inclusionsExpanded: true,
    exclusionsExpanded: true,
  })
  const selectedShop = useSelector((state) => state.selectedShop)
  const toggleSection = (type) =>
    setExpanded({
      ...expandedSections,
      [type]: !expandedSections[type],
    })
  useEffect(() => {
    if(id) {
      setIsLoading(true)
      API(`shop/${selectedShop ? selectedShop.slug : slug}/products/${id}/`)
        .get()
        .then((course) => {
          setCourse(course)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [id])

  if(!isOpen) {
    return null
  }

  const { activity } = course
  const duration = get(activity, 'duration.duration')
  const durationTitle = get(activity, 'duration.durationType.title')
  const thumbnail = get(course, 'thumbnails.FeaturedDealsBigMobileLand')
  const exclusions = get(activity, 'exclusions.length')
  const inclusions = get(activity, 'inclusions.length')
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className='Modal react-itinerary-modal itinerary-modal'
      overlayClassName='Overlay'
      contentLabel='Course details'
    >
      <div className={`itinerary-page-new ${isLoading ? 'loading' : ''}`}>
        <div className='itinerary-page__modal-header'>
          <span className='btn-close' onClick={onClose}>
            &times;
          </span>
        </div>
        <Loader />
        <div className='itinerary-popup-content'>
          {activity ? (
            <ActivityDescription
              title={course.title}
              activity={activity}
              thumbnail={thumbnail}
              duration={duration}
              durationTitle={durationTitle}
              inclusions={inclusions}
              exclusions={exclusions}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
            />
          ) : (
            <NoActivityCourse
              thumbnail={thumbnail}
              title={course.title}
              requirements={course.requirements}
              inclusions={course.whatsIncluded}
              exclusions={course.whatsNotIncluded}
              description={course.description}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

const ActivityDescription = ({
  title,
  activity,
  thumbnail,
  duration,
  durationTitle,
  inclusions,
  exclusions,
  expandedSections,
  toggleSection,
}) => (
  <Fragment>
    <div className='main-title'>
      <h2>{title}</h2>
      <p className='sub activity-duration'>
        <i className='icon-clock font-icons' />
        <FormattedMessage id='activity.duration' defaultMessage='Duration' />
        <span>
          :&nbsp;{duration} {durationTitle}
        </span>
        {duration > 1 && 's'}
      </p>
      {activity.numberOfDives && (
        <p className='sub activity-dives'>
          <i className='dive-center-icon font-icons' />
          <FormattedMessage
            id='activity.including_dives'
            values={{
              qty: activity.numberOfDives,
            }}
            defaultMessage='Including: { qty, plural, one {# dive} other {# dives}}'
          />
        </p>
      )}
    </div>
    {thumbnail && (
      <div className='activity-image'>
        <img src={thumbnail} alt='image' />
      </div>
    )}
    <div className='itinerary-section'>
      <p>{activity.shortDescription}</p>
      {activity.highlights && (
        <ul className='circled-list'>
          {activity.highlights.map((highlight, index) => (
            <li key={index}>{highlight}</li>
          ))}
        </ul>
      )}
    </div>

    {activity.requirements && (
      <div className='itinerary-section'>
        <h4>
          <FormattedMessage id='requirements' defaultMessage='Requirements' />
        </h4>
        <ul className='req-list'>
          <li>
            <FormattedMessage
              tagName='strong'
              id='activity.min-age'
              defaultMessage='Min age'
            />
            <span>
              :&nbsp;
              {activity.requirements.minAge
                ? activity.requirements.minAge + 'y'
                : 'n/a'}
            </span>
          </li>
          <li>
            <FormattedMessage
              tagName='strong'
              id='activity.min-certification'
              defaultMessage='Min certification'
            />
            <span>
              :&nbsp;
              {get(activity.requirements.minCertification, 'title', 'n/a')}
            </span>
          </li>
          <li>
            <FormattedMessage
              id='activity.min-dives-count'
              tagName='strong'
              defaultMessage='Min # logged dives'
            />
            <span>
              :&nbsp;
              {get(activity, 'requirements.minDivesCount.title', 'n/a')}
            </span>
          </li>
        </ul>
        {activity.otherRequirements && <p>{activity.otherRequirements}</p>}
      </div>
    )}

    {!!inclusions && (
      <div className='itinerary-section collapsed-section'>
        <h4
          onClick={() => toggleSection('inclusionsExpanded')}
          className={expandedSections.inclusionsExpanded ? 'active' : ''}
        >
          <FormattedMessage
            id='whats_included'
            defaultMessage='What`s included'
          />
        </h4>
        {expandedSections.inclusionsExpanded && (
          <div className='itinerary-section-details'>
            {activity.inclusions.map((inclusion) => {
              return <p key={inclusion.title}>{inclusion.title}</p>
            })}
          </div>
        )}
      </div>
    )}
    {!!exclusions && (
      <div className='itinerary-section collapsed-section'>
        <h4
          onClick={() => toggleSection('exclusionsExpanded')}
          className={expandedSections.exclusionsExpanded ? 'active' : ''}
        >
          <FormattedMessage
            id='whats_not_included'
            defaultMessage='What`s not included'
          />
        </h4>
        {expandedSections.exclusionsExpanded && (
          <div className='itinerary-section-details'>
            {activity.exclusions.map((exclusion) => {
              return <p key={exclusion.title}>{exclusion.title}</p>
            })}
          </div>
        )}
      </div>
    )}
    <div className='itinerary-section'>
      <h4>
        <FormattedMessage
          id='activity.whatToExpect'
          defaultMessage='What to expect'
        />
      </h4>
      <p dangerouslySetInnerHTML={{ __html: activity?.whatToExpect || 'n/a' }} />
    </div>
  </Fragment>
)

const NoActivityCourse = ({
  thumbnail,
  title,
  requirements,
  inclusions,
  exclusions,
  description,
}) => (
  <Fragment>
    <div className='main-title'>
      <h2>{title}</h2>
    </div>
    {thumbnail && (
      <div className='activity-image'>
        <img src={thumbnail} alt='image' />
      </div>
    )}

    {requirements && (
      <div className='itinerary-section'>
        <h4>
          <FormattedMessage
            id='activity.requirements'
            defaultMessage='Requirements'
          />
        </h4>
        <ul className='req-list'>
          {requirements.split('\n').map((req) => (
            <li>{req}</li>
          ))}
        </ul>
      </div>
    )}

    {inclusions && (
      <div className='itinerary-section'>
        <h4>
          <FormattedMessage
            id='whats_included'
            defaultMessage='What`s included'
          />
        </h4>
        <ul className='req-list'>
          {inclusions.split('\n').map((req) => (
            <li>{req}</li>
          ))}
        </ul>
      </div>
    )}
    {exclusions && (
      <div className='itinerary-section'>
        <h4>
          <FormattedMessage
            id='whats_not_included'
            defaultMessage='What`s not included'
          />
        </h4>
        <ul className='req-list'>
          {exclusions.split('\n').map((req) => (
            <li>{req}</li>
          ))}
        </ul>
      </div>
    )}

    {description && (
      <div className='itinerary-section'>
        <h4>
          <FormattedMessage
            id='activity.whatToExpect'
            defaultMessage='What to expect'
          />
        </h4>
        <p>{description}</p>
      </div>
    )}
  </Fragment>
)

export default CourseDescriptionPopup
